import { api } from "@/api";
import { format } from "date-fns";
import { AxiosInstance, AxiosStatic } from "axios";
import { useNotificationsStore } from "@/stores/notifications";
import { useGeneralStore } from "@/stores/generalStore";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useUserStore } from "@/stores/userStore";
import { useAutosavesStore } from "@/stores/autosaves";
import { useI18n } from "vue-i18n";
import { ref, Ref, getCurrentInstance } from "vue";
import { getEmbedderHostname } from "@topol/utils";

import {
  IAutosaves,
  IGroupedAutosaves,
  AutosavesSchema,
} from "@/schemas/autosave/autosaveSchema";

export default () => {
  const app = getCurrentInstance();

  if (app === null) {
    throw new Error("App is not defined");
  }

  const $http: AxiosStatic | AxiosInstance =
    app.appContext.config.globalProperties.$http;

  const notificationsStore = useNotificationsStore();
  const generalStore = useGeneralStore();
  const mjmlStore = useMjmlStore();
  const userStore = useUserStore();
  const autosavesStore = useAutosavesStore();
  const { t } = useI18n();

  const autosaves: Ref<IAutosaves> = ref([]);

  const loadAutosaves = () => {
    autosavesStore.loading = true;
    autosaves.value = [];

    let params = {};

    if (generalStore.type !== "standalone") {
      params = getParams();
    }

    if (api.AUTOSAVES) {
      $http
        .get(api.AUTOSAVES, { params: params })
        .then((res) => {
          if (res.data.success === true) {
            const { error, data, success } = AutosavesSchema.safeParse(
              res.data.data
            );
            if (!success) {
              console.error("Loading autosaves failed", error.issues);
              notificationsStore.createNotification({
                title: "Loading autosaves failed",
                type: "error",
                text: error.message,
              });
              autosavesStore.loading = false;
              return;
            }
            autosaves.value = data;
            autosavesStore.groupedAutosaves = getGroupedAutosaves();
          } else {
            notificationsStore.createNotification({
              title: "Loading autosaves failed",
              type: "error",
              text: "",
            });
          }
          autosavesStore.loading = false;
        })
        .catch(() => {
          autosavesStore.loading = false;

          notificationsStore.createNotification({
            title: "Loading autosaves failed",
            type: "error",
            text: "",
          });
        });
    }
  };

  const getAutosave = async (key: string) => {
    let params = {};
    const failTrans = t("layout.load_template_error");

    if (generalStore.type !== "standalone") {
      params = getParams();
    }

    try {
      const res = await $http.get(api.GET_AUTOSAVE + "/" + key, {
        params: params,
      });
      if (res.data.success === true) {
        let template;

        // Remove double stringification
        // Keep this for backwards compatibility
        // https://ecomail.atlassian.net/browse/TE-842
        if (typeof res.data.data === "string") {
          const parsedData = JSON.parse(res.data.data);
          template = JSON.parse(parsedData.template);
        } else {
          template = JSON.parse(res.data.data.template);
        }
        return template;
      }
    } catch (error) {
      console.error("Loading autosave failed", error);
      notificationsStore.createNotification({
        title: failTrans,
        type: "error",
        text: "",
      });
    }
  };

  const loadAutosaveFromApi = async (key: string) => {
    mjmlStore.clearSelected();
    generalStore.loadingTemplate = true;

    const successTrans = t("layout.template_loaded");
    const failTrans = t("layout.load_template_error");
    try {
      const template = await getAutosave(key);
      if (template) {
        generalStore.loadingTemplate = false;
        mjmlStore.setMjml(template);
        notificationsStore.createNotification({
          type: "success",
          text: successTrans,
        });
      } else {
        notificationsStore.createNotification({
          title: failTrans,
          type: "error",
          text: "",
        });
      }
    } catch (error) {
      notificationsStore.createNotification({
        title: failTrans,
        type: "error",
        text: "",
      });

      generalStore.loadingTemplate = false;
    }
  };

  const save = () => {
    generalStore.saving = true;

    const params: any = {
      definition: JSON.stringify(mjmlStore.getMjml()),
    };

    if (generalStore.type !== "standalone") {
      params.key = mjmlStore.authorize.apiKey;
      params.hostname = getEmbedderHostname();
      params.templateId = mjmlStore.templateId;
      params.uuid = mjmlStore.authorize.userId;
    }

    if (userStore.currentUser.userId) {
      params.current_user_id = userStore.currentUser.userId;
    }

    if (api.AUTOSAVE === undefined) {
      return;
    }

    $http
      .post(api.AUTOSAVE, params)
      .then(() => {
        generalStore.saving = false;
        mjmlStore.edittedWithoutSave = false;
        loadAutosaves();
      })
      .catch(() => {
        generalStore.saving = false;

        notificationsStore.createNotification({
          title: "Saving autosave failed",
          type: "error",
          text: "",
        });
      });
  };

  function getGroupedAutosaves(): IGroupedAutosaves {
    const groupedAutosaves: IGroupedAutosaves = {};

    for (const autosave of autosaves.value) {
      const formatedDateKey = format(new Date(autosave.time), "y-MM-dd");

      if (!groupedAutosaves[formatedDateKey]) {
        groupedAutosaves[formatedDateKey] = [];
      }
      groupedAutosaves[formatedDateKey].push(autosave);
    }

    return groupedAutosaves;
  }

  function getParams() {
    return {
      key: mjmlStore.authorize.apiKey,
      hostname: getEmbedderHostname(),
      templateId: mjmlStore.templateId,
      uuid: mjmlStore.authorize.userId,
    };
  }

  return {
    loadAutosaves,
    loadAutosaveFromApi,
    getAutosave,
    save,
  };
};
